import ActionButton from 'components/ActionButton';
import AddInventoryItemModal from 'components/AddInventoryItemModal';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import EditActionForTable from 'components/EditActionForTableData';
import CustomLink from 'components/TableLink';
import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { addSubscriptionInventoryItem, getSubscriptionInventoryItems, removeSubscriptionInventoryItem, updateSubscriptionInventoryItem } from 'services';
import { DATE_FORMATS, formatDate, showError, stringifyAndParseObj } from 'utils';
import styles from './index.module.scss';

const SubscriptionInventoryItems = (props) => {
  const { subscriptionId = '' } = props;
  const [subscriptionInventoryItems, setSubscriptionInventoryItems] = useState([]);
  const [addInventoryItemModalOpen, setAddInventoryItemModalOpen] = useState(false);
  const [updateInventoryItemModalOpen, setUpdateInventoryItemModalOpen] = useState(false);
  const [inventoryItemToEdit, setInventoryItemToEdit] = useState({}) as any;

  const getSubscriptionInventoryItemsFn = async () => {
    try {
      const response = await getSubscriptionInventoryItems({ subscriptionId });
      const parsedresponse = stringifyAndParseObj(response);
      setSubscriptionInventoryItems(parsedresponse);
    } catch (e) {
      showError(e);
    }
  };

  useEffect(() => {
    getSubscriptionInventoryItemsFn();
  }, []);

  const removeItem = async(itemId) => {
    await removeSubscriptionInventoryItem(itemId);
    getSubscriptionInventoryItemsFn();
  };

  const columns = useMemo(
    () => [
      {
        name: 'Item Id',
        selector: 'inventoryItem.objectId',
        cell: (row) => (
          <CustomLink
            text={row.objectId}
            linkTo={`/inventory-items/${row.inventoryItem.objectId}`}
          />
        ),
      },
      {
        name: 'Name',
        selector: 'inventoryItem.name',
      },
      {
        name: 'Price($)',
        selector: 'inventoryItem.price',
      },
      {
        name: 'Quantity',
        selector: 'quantity',
      },
      {
        name: 'Created At',
        selector: 'createdAt',
        format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
      },
      {
        name: 'Action',
        selector: '',
        sortable: true,
        // eslint-disable-next-line react/display-name
        cell: (row: Record<string, any>) => (
          <div className={'d-flex flex-row'}>
            <DeleteActionForTable
              onClick={() => {
                removeItem(row?.objectId);
              }}
            />
            <div className='ml-4'>
              <EditActionForTable onClick={() => {
                setInventoryItemToEdit(row); 
                setUpdateInventoryItemModalOpen(true);
              }} />
            </div>
          </div>
        ),
      },
    ], [],
  );

  const getHeader = () => {
    return (
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>Inventory Item</div>
        <ActionButton
          active={true}
          isLoading={false}
          onClick={toggleAddInventoryItemModal}
          text={
            <>
              Inventory Item <span className={'ml-3'}>+</span>
            </>
          } 
        />
      </div>
    );
  };

  const toggleAddInventoryItemModal = () => setAddInventoryItemModalOpen(!addInventoryItemModalOpen);
  const toggleUpdateInventoryItemModal = () => setUpdateInventoryItemModalOpen(!updateInventoryItemModalOpen);

  const onAddSubscriptionInventoryItem = async(params) => {
    const { item, quantity } = params;
    if(!item?.id){
      showError('Please select an item.');
      return;
    }
    if(!quantity){
      showError('Please add quantity.');
      return;
    }
    try {
      await addSubscriptionInventoryItem({ subscriptionId, quantity, inventoryItemId: item?.id });
      getSubscriptionInventoryItemsFn();
      toggleAddInventoryItemModal();
    } catch (e) {
      showError(e);
    }
  };

  const onUpdateSubscriptionInventoryItem = async(params) => {
    const { quantity } = params;
    if(!quantity){
      showError('Please add quantity.');
      return;
    }
    try {
      await updateSubscriptionInventoryItem({ quantity, id: inventoryItemToEdit?.objectId });
      getSubscriptionInventoryItemsFn();
      toggleUpdateInventoryItemModal();
    } catch (e) {
      showError(e);
    }
  };
  
  return (
    <div className={styles['inventory-items-container']}>
      {getHeader()}
      {addInventoryItemModalOpen && (
        <AddInventoryItemModal
          onSubmit={onAddSubscriptionInventoryItem}
          toggleModal={toggleAddInventoryItemModal}
          heading="Add A New Inventory Item"
          showQuantityInput 
        />
      )}
      {updateInventoryItemModalOpen && (
        <AddInventoryItemModal
          onSubmit={onUpdateSubscriptionInventoryItem}
          toggleModal={toggleUpdateInventoryItemModal}
          heading="Update Inventory Item"
          showQuantityInput 
          quantity={inventoryItemToEdit?.quantity}
          item={inventoryItemToEdit?.inventoryItem}
          disableSearch
        />
      )}
      <DataTable
        noHeader
        data={subscriptionInventoryItems}
        columns={columns}
        customStyles={tableStyles}
      />
    </div>
  );
};

export default SubscriptionInventoryItems;
